import { Color } from '@persona-id/design-tokens';

import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { CaseModule, generateModuleToken } from 'dashboard/models/case-template';
import { InsightSummaryIcons } from 'dashboard/modules/InsightsSummary/model';
import { KeyValueModuleDisplayFormat } from 'dashboard/modules/KeyValue';
import { ResourceInquiryAttribute } from 'dashboard/modules/ResourceInquiry';
import { GovernmentIdVerificationModuleAttribute } from 'dashboard/modules/ResourceVerification/model';
import { ApiModel } from 'lib/interfaces/api';

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const generateDefaultCaseModuleConfiguration = (moduleType: ModuleTypes): CaseModule => {
  switch (moduleType) {
    case ModuleTypes.CaseInfo:
      return {
        type: ModuleTypes.CaseInfo,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 30 },
        'module-layout': {
          'dynamic-height': true,
        },
        attributes: ['assignee', 'reference-ids', 'accounts', 'sla'],
      };
    case ModuleTypes.CaseComments:
      return {
        type: ModuleTypes.CaseComments,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.CaseEmail:
      return {
        type: ModuleTypes.CaseEmail,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'default-from-email-address': '',
        'module-layout': {
          'dynamic-height': false,
        },
      };
    case ModuleTypes.Checklist:
      return {
        type: ModuleTypes.Checklist,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 30 },
        'module-layout': {
          'dynamic-height': true,
        },
        title: 'Checklist',
        items: [],
      };
    case ModuleTypes.Tags:
      return {
        type: ModuleTypes.Tags,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 20 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.AccountSimilaritySelfie:
      return {
        type: ModuleTypes.AccountSimilaritySelfie,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 20 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.EventTimeline:
      return {
        type: ModuleTypes.EventTimeline,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.Location:
      return {
        type: ModuleTypes.Location,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 60 },
        'module-layout': {
          'dynamic-height': true,
        },
        'custom-locations': undefined,
      };
    case ModuleTypes.Device:
      return {
        type: ModuleTypes.Device,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 30 },
        'module-layout': {
          'dynamic-height': true,
        },
        attributes: [
          'type',
          'os',
          'browser',
          'browser-fingerprint',
          'device-handoff-method',
          'device-vendor-id',
          'ip-address',
          'threat-level',
          'country-name',
          'region-name',
          'latitude',
          'longitude',
          'is-tor',
          'is-proxy',
          'is-datacenter',
          'is-isp',
          'is-vpn',
        ],
      };
    case ModuleTypes.KeyValue:
      return {
        type: ModuleTypes.KeyValue,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 70 },
        'module-layout': {
          'dynamic-height': true,
        },
        'display-format': KeyValueModuleDisplayFormat.Grid,
        title: 'Key Value',
        'key-label': 'Keys',
        'value-label': 'Values',
        'preview-data': {
          _id: '620c4248b',
          guid: '11f01191-86b8-4a11-97ab-c14b9e257da4',
          balance: '$1,081.70',
        },
        'field-key': '',
      };
    case ModuleTypes.Account:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'accounts',
        'default-accordion-open': true,
        'view-layout': 'list',
        attributes: [],
      };
    case ModuleTypes.Inquiry:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 70 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'inquiries',
        'default-accordion-open': true,
        'view-layout': 'list',
        attributes: [
          ResourceInquiryAttribute.Photos,
          ResourceInquiryAttribute.Name,
          ResourceInquiryAttribute.GovernmentIDNumber,
          ResourceInquiryAttribute.BirthDate,
          ResourceInquiryAttribute.Address,
          ResourceInquiryAttribute.ExpirationDate,
          ResourceInquiryAttribute.EmailAddress,
          ResourceInquiryAttribute.PhoneNumber,
          ResourceInquiryAttribute.SessionLocation,
          ResourceInquiryAttribute.Fields,
          ResourceInquiryAttribute.DocumentExtraction,
        ],
      };
    case ModuleTypes.Report:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'reports',
        'default-accordion-open': true,
        'view-layout': 'list',
        'report-template-type': ApiModel.ReportTemplateWatchlist,
        attributes: [],
      };
    case ModuleTypes.ReportRun:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'report-runs',
        'default-accordion-open': true,
        'view-layout': 'list',
        'report-template-type': ApiModel.ReportTemplateCustomList,
        attributes: [],
      };
    case ModuleTypes.Transaction:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'transactions',
        'default-accordion-open': true,
        'view-layout': 'list',
        'txn-type': null,
        attributes: [],
      };
    case ModuleTypes.Verification:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 70 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'verifications',
        'default-accordion-open': true,
        'view-layout': 'list',
        'verification-type': ApiModel.VerificationGovernmentId,
        'verification-templates': [],
        attributes: [
          GovernmentIdVerificationModuleAttribute.Photos,
          GovernmentIdVerificationModuleAttribute.Id,
          GovernmentIdVerificationModuleAttribute.FullName,
          GovernmentIdVerificationModuleAttribute.BirthDate,
          GovernmentIdVerificationModuleAttribute.IssuingCountry,
          GovernmentIdVerificationModuleAttribute.IssueDate,
          GovernmentIdVerificationModuleAttribute.ExpirationDate,
          GovernmentIdVerificationModuleAttribute.CaptureMethod,
          GovernmentIdVerificationModuleAttribute.CaptureMethodBack,
          GovernmentIdVerificationModuleAttribute.Address,
          GovernmentIdVerificationModuleAttribute.Nationality,
          GovernmentIdVerificationModuleAttribute.Checks,
        ],
      };
    case ModuleTypes.Graph:
      return {
        type: ModuleTypes.Graph,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 60 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.Graphv2:
      return {
        type: ModuleTypes.Graphv2,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 70 },
        'expected-graph-query-template': [],
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.ListMatches:
      return {
        type: ModuleTypes.ListMatches,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.CaseAttachments:
      return {
        type: ModuleTypes.CaseAttachments,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.DataDisplay:
      return {
        type: ModuleTypes.DataDisplay,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
        title: 'Sample Data',
        sections: [
          {
            title: 'Section A',
            attributes: [
              {
                label: 'Attribute 1',
                value: 'Attribute Value',
              },
              {
                label: 'Attribute 2',
                value: 'Attribute Value',
                obscurable: true,
              },
            ],
          },
        ],
        'collapse-empty-fields': false,
      };
    case ModuleTypes.RelatedCases:
      return {
        type: ModuleTypes.RelatedCases,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.Button:
      return {
        type: ModuleTypes.Button,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 2, height: 6 },
        'module-layout': {
          'dynamic-height': false,
        },
        name: 'Button',
        'action-id': '',
        variant: 'basic',
      };
    case ModuleTypes.LayoutByAccount:
      return {
        type: ModuleTypes.LayoutByAccount,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 117 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'accounts',
        resources: [
          {
            type: ModuleTypes.Resource,
            token: generateModuleToken(),
            'field-key': 'inquiries',
            attributes: [
              ResourceInquiryAttribute.Photos,
              ResourceInquiryAttribute.Name,
              ResourceInquiryAttribute.GovernmentIDNumber,
              ResourceInquiryAttribute.BirthDate,
              ResourceInquiryAttribute.Address,
              ResourceInquiryAttribute.ExpirationDate,
              ResourceInquiryAttribute.EmailAddress,
              ResourceInquiryAttribute.PhoneNumber,
              ResourceInquiryAttribute.SessionLocation,
              ResourceInquiryAttribute.Fields,
              ResourceInquiryAttribute.DocumentExtraction,
            ],
            'default-accordion-open': false,
            'hide-empty-attributes': true,
            position: { x: 0, y: 0, width: 12, height: 50 },
            'module-layout': {
              'dynamic-height': false,
            },
          },
          {
            type: ModuleTypes.Resource,
            token: generateModuleToken(),
            'field-key': 'verifications',
            attributes: [
              GovernmentIdVerificationModuleAttribute.Photos,
              GovernmentIdVerificationModuleAttribute.Checks,
            ],
            'verification-type': ApiModel.VerificationGovernmentId,
            'verification-templates': [],
            'default-accordion-open': false,
            position: { x: 0, y: 0, width: 12, height: 50 },
            'module-layout': {
              'dynamic-height': false,
            },
          },
          {
            type: ModuleTypes.Resource,
            token: generateModuleToken(),
            'field-key': 'reports',
            'report-template-type': ApiModel.ReportTemplateWatchlist,
            'default-accordion-open': false,
            attributes: [],
            position: { x: 0, y: 0, width: 12, height: 50 },
            'module-layout': {
              'dynamic-height': false,
            },
          },
        ],
      };
    case ModuleTypes.Input:
      return {
        type: ModuleTypes.Input,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 18 },
        'module-layout': {
          'dynamic-height': false,
        },
        label: 'Sample Input Label',
        'field-key': '',
      };
    case ModuleTypes.InputSelect:
      return {
        type: ModuleTypes.InputSelect,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 18 },
        'module-layout': {
          'dynamic-height': false,
        },
        label: 'Sample Select Label',
        'field-key': '',
      };
    case ModuleTypes.Markdown:
      return {
        type: ModuleTypes.Markdown,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 30 },
        'module-layout': {
          'dynamic-height': false,
        },
        markdown: '# Heading 1 \n ## Heading 2 \n ### Heading 3 \n - bulleted \n - list',
      };
    case ModuleTypes.AISummary:
      return {
        type: ModuleTypes.AISummary,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 30 },
        'module-layout': {
          'dynamic-height': true,
        },
        'user-prompt': '',
      };
    case ModuleTypes.Sar:
      return {
        type: ModuleTypes.Sar,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.FieldUpdate:
      return {
        type: ModuleTypes.FieldUpdate,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 24 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.InsightsSummary:
      return {
        type: ModuleTypes.InsightsSummary,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 24 },
        'module-layout': {
          'dynamic-height': true,
        },
        label: 'Insights summary',
        insights: [
          {
            label: 'Insight one',
            states: [
              {
                icon: InsightSummaryIcons.CHECK,
                color: Color.CoreGreen600,
                condition: {
                  'resolution-mechanism': {
                    'resolution-type': 'template-literal',
                    'serialized-code': btoa('{{ true }}'),
                  },
                },
                'display-value': {
                  'resolution-mechanism': {
                    'resolution-type': 'template-literal',
                    'serialized-code': btoa('Passed'),
                  },
                },
              },
              {
                icon: InsightSummaryIcons.ALERT,
                color: Color.CoreYellow600,
                condition: {
                  'resolution-mechanism': {
                    'resolution-type': 'template-literal',
                    'serialized-code': btoa('{{ true }}'),
                  },
                },
                'display-value': {
                  'resolution-mechanism': {
                    'resolution-type': 'template-literal',
                    'serialized-code': btoa('Needs Review'),
                  },
                },
              },
              {
                icon: InsightSummaryIcons.ALERT,
                color: Color.CoreRed600,
                condition: {
                  'resolution-mechanism': {
                    'resolution-type': 'template-literal',
                    'serialized-code': btoa('{{ true }}'),
                  },
                },
                'display-value': {
                  'resolution-mechanism': {
                    'resolution-type': 'template-literal',
                    'serialized-code': btoa('Failed'),
                  },
                },
              },
            ],
          },
        ],
      };
    case ModuleTypes.Table:
      return {
        type: ModuleTypes.Table,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 24 },
        'module-layout': {
          'dynamic-height': true,
        },
        title: 'Table',
        'case-query-dependencies': [],
        data: '',
      };
    case ModuleTypes.Iframe:
      return {
        type: ModuleTypes.Iframe,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 30 },
        'module-layout': {
          'dynamic-height': false,
        },
        title: {
          'resolution-mechanism': {
            'resolution-type': 'template-literal',
            'serialized-code': btoa("Google search for {{ 'Persona Identities' }}"),
          },
        },
        url: {
          'resolution-mechanism': {
            'resolution-type': 'template-literal',
            'serialized-code': btoa(
              "https://www.google.com/search?igu=1&q={{ 'Persona Identities' }}",
            ),
          },
        },
      };
    case ModuleTypes.DataTable:
      return {
        type: ModuleTypes.DataTable,
        token: generateModuleToken(),
        columns: [],
        position: { x: 0, y: 0, width: 8, height: 30 },
        'module-layout': {
          'dynamic-height': false,
        },
        'variable-token': null,
      };
  }
};
